import { getChallenge, initUploadFile } from 'client/actions/api';
import type { FormDataT } from 'client/components/common/StoryCard/Form/types';
import { uploadToS3 } from 'src/routes/client/utils/upload-to-s3';
import { appLog } from 'utils/logger';

import { captureMessage } from '@sentry/react';
import type { HandleThunkActionCreator } from 'react-redux';
import { findNonce } from './utils';
import { FieldBBErrorType } from '../constants';

const log = appLog.extend('file-upload-to-bucket');

export type FileItem = FormDataT['fieldId'] & { id: string };

interface UploadParams {
	storyId: string;
	cardId: string;
	getChallengeFn: HandleThunkActionCreator<typeof getChallenge>;
	initUploadFileFn: HandleThunkActionCreator<typeof initUploadFile>;
	setFieldError?: (fieldId: string, errorType: FieldBBErrorType) => void;
}

export async function uploadFormFiles(fileItems: FileItem[], params: UploadParams): Promise<FileItem[]> {
	const { storyId, cardId, getChallengeFn, initUploadFileFn, setFieldError } = params;
	const challenge = await getChallengeFn({ type: 'high' });

	if (!challenge) {
		// TODO: handle this case properly, e.g. show a notification to the user instead.
		//       For now just set a generic error for the first file to have some feedback in the UI.
		setFieldError?.(fileItems[0].id, FieldBBErrorType.genericError);
		captureMessage('File upload: Challenge not found', { level: 'error', tags: { module: 'file-upload' } });
		throw new Error('Challenge not found');
	}

	const nonce = findNonce(challenge.prefix, challenge.difficulty);

	const results = await Promise.all(
		fileItems.map(async (item): Promise<FileItem> => {
			const contentType = item.file?.type ?? '';
			const fileSize = item.file?.size ?? 0;
			const fileName = item.file?.name ?? 'unknown';

			const initResult = await initUploadFileFn(
				{ storyId, cardId, inputId: item.id },
				{
					contentType,
					size: fileSize,
					challenge: { id: challenge.id, nonce },
				}
			);

			if (!initResult) {
				setFieldError?.(item.id, FieldBBErrorType.genericError);
				captureMessage('File upload: Init upload failed', {
					level: 'error',
					tags: { module: 'file-upload', contentType },
					extra: { contentType, fileSize, fileName, inputId: item.id },
				});
				throw new Error(`Init upload failed for input "${item.id}"`);
			}

			const uploadResult = await uploadToS3(item.file!, initResult);
			log(`uploadResult for ${item.id}`, uploadResult);

			return {
				...item,
				data: uploadResult.key,
			};
		})
	);

	return results;
}
